import { CONFIG_TYPES } from "./constants";

// * Where the room graph gets its sensor data: EYE's own registers, RipeWise, or one with the other as fallback.
export const RIPEWISE_SENSOR_DATA_SOURCES = ["eye", "ripewise", "eye_first", "ripewise_first"] as const;

// * The program's general settings that the per-fruit limits set (read-only in the program form).
export const FRUIT_LIMIT_KEYS = ["T_Air_Min", "T_Air_Max", "T_Prod_Min", "T_Prod_Max"] as const;
export type FruitLimitKey = typeof FRUIT_LIMIT_KEYS[number];
export type FruitLimits = Partial<Record<FruitLimitKey, number>>;

export type RipewiseConfig = {
    enabled: boolean;
    tenant_id: string;
    control: boolean;
    sensor_data: typeof RIPEWISE_SENSOR_DATA_SOURCES[number];
    fruit_limits: Array<{ fruit_type?: string } & Partial<Record<FruitLimitKey, number | string | null>>>;
};

// * Without a "ripewise" tenant config RipeWise is off (the backend uses the same defaults).
export const defaultRipewiseConfig: RipewiseConfig = {
    enabled: false,
    tenant_id: "",
    control: false,
    sensor_data: "eye",
    fruit_limits: [],
};

// * The tenant's RipeWise config over the defaults, from its raw tenant configs (type and value "ripewise", like the backend).
export const getRipewiseConfig = (configs?: { type: string; value: string; active?: boolean; config: any }[]): RipewiseConfig & { can_control: boolean } => {
    const stored = (configs || []).find((i) => i.type === CONFIG_TYPES.RIPEWISE && i.value === CONFIG_TYPES.RIPEWISE && i.active !== false)?.config || {};
    const config = { ...defaultRipewiseConfig, ...stored };
    return { ...config, can_control: config.enabled === true && config.control === true };
};

// * The configured limits of a fruit, only the keys that hold a number (the backend reads them the same way).
export const fruitLimitsOf = (fruitLimits: RipewiseConfig["fruit_limits"] | undefined, fruitType: string | undefined): FruitLimits => {
    const entry = fruitType ? (fruitLimits || []).find((i) => i?.fruit_type === fruitType) : undefined;
    return FRUIT_LIMIT_KEYS.reduce((limits, key) => {
        const raw = entry?.[key];
        const value = raw === "" || raw === null || raw === undefined || typeof raw === "boolean" ? NaN : Number(raw);
        if (Number.isFinite(value)) limits[key] = value;
        return limits;
    }, {} as FruitLimits);
};
